//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { useEffect, useState } from 'react'

import { ContentCopy, ExpandMore, FilterList, KeyboardArrowUp, OpenInNew } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Switch,
  Typography,
  useTheme,
} from '@mui/material'
import { aprToDisplay, erc20WeiToCommaDecString } from '@shared/conversions'

import ButtonModal from '@react/components/ButtonModal'
import ViewLogo from '@react/components/elements/ViewLogo'
import { PoolDynamicOverviewCTO } from '@shared/PoolDynamicCTO'
import {
  PoolAccountCTO,
  PoolBaseOverviewCTO,
  PoolFlexOverviewCTO,
  poolIsOpenForInvestment,
  PoolOverviewCTO,
} from '@shared/sharedTypes'
import { Link, useNavigate } from 'react-router-dom'

import LenderDepositToPool from './LenderDepositToPool'

import { PoolTypeEnum } from '@shared/Enums'

// Map pool types to protocol versions
const PROTOCOL_VERSION_MAP: Partial<Record<PoolTypeEnum, string>> = {
  [PoolTypeEnum.Term]: '3.0',
  [PoolTypeEnum.FlexibleRate]: '4.0',
  [PoolTypeEnum.Dynamic]: '5.0',
}

import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'

import { buildPills } from '@react/components/elements/InterestPills'
import PoolListSkeleton from '@react/components/pools/PoolListSkeleton'
import { useOtChainState, useOtChainTimestamp, useOtSelector } from '@react/redux/store'

import { displayName } from '@shared/PoolBaseCTO'
import { UnderlyingCollateralCTO } from '@shared/CollateralSetCTO'
import { buildLenderPoolSummary } from './lenderUtils'

// Reusable PoolDetailPill component
interface PoolDetailPillProps {
  children: React.ReactNode
  theme: any
}

function PoolDetailPill({ children, theme }: PoolDetailPillProps): React.ReactElement {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 2,
        py: 0.25,
        backgroundColor: '#ffffff',
        borderRadius: '1rem',
        fontSize: '10px',
        fontWeight: 500,
        border: `1px solid ${theme.palette.background.muted}`,
        color: '#666',
      }}
    >
      {children}
    </Box>
  )
}

// Helper function to get the maximum interest rate from pool data for sorting
function getMaximumInterestRate(pool: PoolBaseOverviewCTO): number {
  if (pool.poolType === PoolTypeEnum.Term) {
    const termPool = pool as PoolOverviewCTO
    const finalizedApr = Number(termPool?.activeLoanState?.finalizedApr || 0)
    const indicativeApr = Number(termPool?.requestedLoanState?.indicativeApr || 0)
    return Math.max(finalizedApr, indicativeApr)
  } else if (pool.poolType === PoolTypeEnum.FlexibleRate) {
    const flexPool = pool as PoolFlexOverviewCTO
    return Number(flexPool?.interestRate || 0)
  } else if (pool.poolType === PoolTypeEnum.Dynamic) {
    const dynamicPool = pool as PoolDynamicOverviewCTO
    const dynamicInterestRate = Number(dynamicPool?.interestRate || 0)
    const dynamicIndicativeRate = Number(dynamicPool?.indicativeInterestRate || 0)
    return Math.max(dynamicInterestRate, dynamicIndicativeRate)
  }
  return 0
}

// Helper function to get user's principal earning interest for a pool
function getMyPrincipalEarningInterest(
  pool: PoolBaseOverviewCTO,
  accountAddr: string | undefined,
  poolAccountCTOs: any
): number {
  if (!accountAddr || !poolAccountCTOs?.[pool.poolAddr]?.[accountAddr]) {
    return 0
  }

  const accountCTO = poolAccountCTOs[pool.poolAddr][accountAddr]

  if (pool.poolType === PoolTypeEnum.FlexibleRate && 'principalEarningInterest' in accountCTO) {
    return Number(accountCTO.principalEarningInterest || 0)
  } else if (pool.poolType === PoolTypeEnum.Dynamic && 'principalEarningInterest' in accountCTO) {
    return Number(accountCTO.principalEarningInterest || 0)
  } else if (pool.poolType === PoolTypeEnum.Term && 'tokenBalance' in accountCTO) {
    return Number(accountCTO.tokenBalance || 0)
  }

  return 0
}

// Helper function to get total principal earning interest for a pool
function getTotalPrincipalEarningInterest(pool: PoolBaseOverviewCTO): number {
  if (pool.poolType === PoolTypeEnum.FlexibleRate) {
    const flexPool = pool as PoolFlexOverviewCTO
    return Number(flexPool.totalPrincipalEarningInterest || 0)
  } else if (pool.poolType === PoolTypeEnum.Dynamic) {
    const dynamicPool = pool as PoolDynamicOverviewCTO
    return Number(dynamicPool.totalOutstandingLoanPrincipal || 0)
  } else if (pool.poolType === PoolTypeEnum.Term) {
    const termPool = pool as PoolOverviewCTO
    return Number(termPool.totalAssets || 0)
  }
  return 0
}

function PoolOpeningRow(props: { poolBaseOverviewCTO: PoolBaseOverviewCTO }): React.ReactElement {
  const { poolBaseOverviewCTO } = props

  const {
    isLoggedIn,
    userConnection: { accountAddr },
    chainConfiguration,
  } = useOtChainState()
  const { accountPoolPairs } = useOtSelector((state) => state.poolState)

  const chainTimestamp = useOtChainTimestamp()
  const [hasAccess, setHasAccess] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const theme = useTheme()
  const poolAccountCTOs = useOtSelector((state) => state.poolState.poolAccountCTOs)
  useEffect(() => {
    if (accountAddr && poolBaseOverviewCTO?.poolAddr && accountPoolPairs[accountAddr]?.[poolBaseOverviewCTO.poolAddr]) {
      setHasAccess(true)
    } else {
      setHasAccess(false)
    }
  }, [accountPoolPairs])

  const navigate = useNavigate()
  let interestPills: React.ReactElement | undefined = undefined
  const poolOverviewCTO = poolBaseOverviewCTO as PoolOverviewCTO | PoolFlexOverviewCTO | PoolDynamicOverviewCTO
  let poolTermOverviewCTO: PoolOverviewCTO | undefined = undefined
  if (poolBaseOverviewCTO.poolType === PoolTypeEnum.Term) {
    poolTermOverviewCTO = poolBaseOverviewCTO as PoolOverviewCTO
    const poolAccountCTO = accountAddr
      ? (poolAccountCTOs?.[poolOverviewCTO.poolAddr]?.[accountAddr] as PoolAccountCTO)
      : undefined
    const investorSummary = buildLenderPoolSummary(poolTermOverviewCTO, poolAccountCTO)
    const apyPill =
      investorSummary.finalizedApr && investorSummary.finalizedApr > 0 ? (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 2,
            py: 0.5,
            backgroundColor: '#e8f5e8',
            color: '#2e7d32',
            borderRadius: '1rem',
            fontSize: '0.875rem',
            fontWeight: 600,
            marginRight: 1,
          }}
        >
          {aprToDisplay(Number(investorSummary.finalizedApr))}% APY
        </Box>
      ) : undefined
    const indicativePill =
      poolOverviewCTO &&
      poolTermOverviewCTO.requestedLoanState.loanAddr !== ETH_ADDR_ZERO &&
      Number(poolTermOverviewCTO?.requestedLoanState?.indicativeApr) !== 0 ? (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 2,
            py: 0.5,
            backgroundColor: '#fff3e0',
            color: '#f57c00',
            borderRadius: '1rem',
            fontSize: '0.875rem',
            fontWeight: 600,
            marginRight: 1,
          }}
        >
          Indicative {aprToDisplay(poolTermOverviewCTO?.requestedLoanState?.indicativeApr)}% APY
        </Box>
      ) : undefined
    interestPills = (
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        {apyPill}
        {indicativePill}
      </Box>
    )
  } else {
    interestPills = buildPills(poolBaseOverviewCTO as PoolFlexOverviewCTO, theme)
  }

  const handleCopyAddress = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(poolBaseOverviewCTO.poolAddr)
  }

  return (
    <Card
      key={poolBaseOverviewCTO.poolAddr}
      onClick={() => navigate(`/pools/${poolBaseOverviewCTO.poolAddr}`)}
      sx={{
        marginBottom: 4,
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
        border: '1px solid transparent',
        transition: 'all 0.1s ease-in-out',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: theme.shadows[4],
          transform: 'translateY(-1px)',
          border: `1px solid ${theme.palette.primary.main}`,
        },
      }}
    >
      <CardContent sx={{ padding: '16px' }}>
        {/* Main content */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', marginBottom: 2 }}>
          <ViewLogo
            size={48}
            style={{
              marginRight: '16px',
              backgroundColor: theme.palette.background.paper,
              borderRadius: '8px',
            }}
            addrOrId={poolBaseOverviewCTO.poolAddr}
          />

          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: 1 }}>
              <Typography variant="h6" fontWeight="bold" marginRight={2}>
                {displayName(poolOverviewCTO)}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>{interestPills}</Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <PoolDetailPill theme={theme}>
                  Liquidity Asset: {poolBaseOverviewCTO.liquidityTokenSymbol}
                </PoolDetailPill>
                <PoolDetailPill theme={theme}>
                  Term:{' '}
                  {poolBaseOverviewCTO.poolType === PoolTypeEnum.Term && poolTermOverviewCTO
                    ? `${poolTermOverviewCTO.activeLoanState?.durationDays || poolTermOverviewCTO.requestedLoanState?.durationDays || 90} days`
                    : 'Flexible'}
                </PoolDetailPill>
                {/* <PoolDetailPill theme={theme}>Collateral: Private Credit</PoolDetailPill> */}
                <PoolDetailPill theme={theme}>
                  Protocol Version: {PROTOCOL_VERSION_MAP[poolBaseOverviewCTO.poolType] || 'N/A'}
                </PoolDetailPill>
              </Box>
            </Box>
          </Box>
          {isLoggedIn && (
            <Box
              sx={{
                textAlign: 'right',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
                <strong>
                  {(() => {
                    const principalAmount = getMyPrincipalEarningInterest(
                      poolBaseOverviewCTO,
                      accountAddr,
                      poolAccountCTOs
                    )
                    if (principalAmount === 0) {
                      return `0.00 ${poolBaseOverviewCTO.liquidityTokenSymbol}`
                    }
                    return `${erc20WeiToCommaDecString(principalAmount)} ${poolBaseOverviewCTO.liquidityTokenSymbol}`
                  })()}
                </strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                My Principal Earning Interest
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {hasAccess &&
              poolIsOpenForInvestment(poolBaseOverviewCTO, chainTimestamp) &&
              !chainConfiguration?.hiddenInvestVaultAddresses?.some(
                (hiddenAddr) => hiddenAddr.toLowerCase() === poolBaseOverviewCTO.poolAddr.toLowerCase()
              ) && (
                <Box onClick={(e) => e.stopPropagation()}>
                  <ButtonModal variant="primary" title="Invest">
                    <LenderDepositToPool poolAddr={poolBaseOverviewCTO.poolAddr}></LenderDepositToPool>
                  </ButtonModal>
                </Box>
              )}
            <Button
              variant="outlined"
              startIcon={<OpenInNew />}
              onClick={(e) => {
                e.stopPropagation()
                const explorerUrl = chainConfiguration?.blockExplorerUrl
                  ? `${chainConfiguration.blockExplorerUrl}address/${poolBaseOverviewCTO.poolAddr}`
                  : ''
                if (explorerUrl) {
                  window.open(explorerUrl, '_blank', 'noopener,noreferrer')
                }
              }}
            >
              View on explorer
            </Button>
          </Box>
          <Button
            variant="text"
            endIcon={
              <ExpandMore sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            }
            onClick={(e) => {
              e.stopPropagation()
              setExpanded(!expanded)
            }}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              color: theme.palette.text.secondary,
              '&:hover': {
                textDecoration: 'underline',
                backgroundColor: 'transparent',
              },
            }}
          >
            More details
          </Button>
        </Box>

        {/* More details accordion */}
        <Accordion
          expanded={expanded}
          onChange={() => setExpanded(!expanded)}
          elevation={0}
          sx={{ backgroundColor: 'transparent', '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ display: 'none' }} />
          <AccordionDetails
            sx={{
              padding: 3,
              backgroundColor: theme.palette.grey[100],
              borderRadius: 2,
              margin: '8px 0',
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ marginBottom: 3 }}>
              Vault Details
            </Typography>

            <Box
              sx={{
                backgroundColor: 'white',
                padding: '8px',
                borderRadius: 2,
                border: `1px solid ${theme.palette.grey[300]}`,
                marginBottom: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
                <strong>
                  {(() => {
                    const totalPrincipalAmount = getTotalPrincipalEarningInterest(poolBaseOverviewCTO)
                    if (totalPrincipalAmount === 0) {
                      return `0.00 ${poolBaseOverviewCTO.liquidityTokenSymbol}`
                    }
                    return `${erc20WeiToCommaDecString(totalPrincipalAmount)} ${poolBaseOverviewCTO.liquidityTokenSymbol}`
                  })()}
                </strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Principal Earning Interest
              </Typography>
            </Box>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Box sx={{ marginBottom: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ marginBottom: 0.5 }}>
                    Vault Address:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {`${poolBaseOverviewCTO.poolAddr.slice(0, 6)}....${poolBaseOverviewCTO.poolAddr.slice(-3)}`}
                    </Typography>
                    <IconButton size="small" onClick={handleCopyAddress}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ marginBottom: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ marginBottom: 0.5 }} display="inline">
                    Benchmark Rate:
                  </Typography>
                  <Typography variant="caption" sx={{ ml: 1, fontWeight: 700 }} display="inline">
                    {poolBaseOverviewCTO.benchmarkRate || 'N/A'}
                  </Typography>
                </Box>

                <Box sx={{ marginBottom: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ marginBottom: 0.5 }} display="inline">
                    Advisory Fee:
                  </Typography>
                  <Typography variant="caption" sx={{ ml: 1, fontWeight: 700 }} display="inline">
                    {poolBaseOverviewCTO.advisoryFeeRate ? `${Number(poolBaseOverviewCTO.advisoryFeeRate)} BPS` : 'N/A'}
                  </Typography>
                </Box>

                <Box sx={{ marginBottom: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ marginBottom: 0.5 }} display="inline">
                    Platform Fee:
                  </Typography>
                  <Typography variant="caption" sx={{ ml: 1, fontWeight: 700 }} display="inline">
                    {poolBaseOverviewCTO.platformFeeRate ? `${Number(poolBaseOverviewCTO.platformFeeRate)} BPS` : 'N/A'}
                  </Typography>
                </Box>

                <Box sx={{ marginBottom: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ marginBottom: 0.5 }} display="inline">
                    Liquidity Fee:
                  </Typography>
                  <Typography variant="caption" sx={{ ml: 1, fontWeight: 700 }} display="inline">
                    {poolBaseOverviewCTO.liquidityFeeRate
                      ? `${Number(poolBaseOverviewCTO.liquidityFeeRate)} BPS`
                      : 'N/A'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ marginBottom: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ marginBottom: 0.5 }} display="inline">
                    Contractual Currency:
                  </Typography>
                  <Typography variant="caption" sx={{ ml: 1, fontWeight: 700 }} display="inline">
                    {poolBaseOverviewCTO.contractualCurrency || 'USD'}
                  </Typography>
                </Box>

                <Box sx={{ marginBottom: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ marginBottom: 0.5 }} display="inline">
                    Min Transfer:
                  </Typography>
                  <Typography variant="caption" sx={{ ml: 1, fontWeight: 700 }} display="inline">
                    {poolBaseOverviewCTO.minimumTransferAmount
                      ? `${poolBaseOverviewCTO.minimumTransferAmount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })} ${poolBaseOverviewCTO.liquidityTokenSymbol}`
                      : 'N/A'}
                  </Typography>
                </Box>

                <Box sx={{ marginBottom: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ marginBottom: 0.5 }} display="inline">
                    Max Transfer:
                  </Typography>
                  <Typography variant="caption" sx={{ ml: 1, fontWeight: 700 }} display="inline">
                    {poolBaseOverviewCTO.maximumTransferAmount
                      ? `${poolBaseOverviewCTO.maximumTransferAmount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })} ${poolBaseOverviewCTO.liquidityTokenSymbol}`
                      : 'N/A'}
                  </Typography>
                </Box>

                <Box sx={{ marginBottom: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ marginBottom: 0.5 }} display="inline">
                    Advance Processing:
                  </Typography>
                  <Typography variant="caption" sx={{ ml: 1, fontWeight: 700 }} display="inline">
                    {poolBaseOverviewCTO.transferInDays ? `${poolBaseOverviewCTO.transferInDays} days` : 'N/A'}
                  </Typography>
                </Box>

                <Box sx={{ marginBottom: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ marginBottom: 0.5 }} display="inline">
                    Withdrawal Processing:
                  </Typography>
                  <Typography variant="caption" sx={{ ml: 1, fontWeight: 700 }} display="inline">
                    {poolBaseOverviewCTO.poolType === PoolTypeEnum.FlexibleRate &&
                    (poolBaseOverviewCTO as PoolFlexOverviewCTO).transferOutDays
                      ? `${(poolBaseOverviewCTO as PoolFlexOverviewCTO).transferOutDays} days`
                      : poolBaseOverviewCTO.poolType === PoolTypeEnum.Dynamic &&
                          (poolBaseOverviewCTO as PoolDynamicOverviewCTO).transferOutDays
                        ? `${(poolBaseOverviewCTO as PoolDynamicOverviewCTO).transferOutDays} days`
                        : 'N/A'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  )
}

export default function LenderPoolOpenings(_props: { limit?: number }): React.ReactElement {
  const { poolOverviewCTOs, isDataReady, poolAccountCTOs, accountPoolPairs } = useOtSelector((state) => state.poolState)
  const { stageConfiguration, chainConfiguration, userConnection, isLoggingIn } = useOtChainState()
  const { accountAddr } = userConnection || {}
  const theme = useTheme()

  // Filter state
  const [currencyFilter, setCurrencyFilter] = useState<string>('all')
  const [withAccessFilter, setWithAccessFilter] = useState<boolean>(true)

  const [collateralFilter, setCollateralFilter] = useState<string>('all')
  const [networkFilter, setNetworkFilter] = useState<string>('all')
  const [sortBy, setSortBy] = useState<string>('alphabetical')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  // Get unique filter options from pool data
  const currencies = Array.from(new Set(Object.values(poolOverviewCTOs).map((pool) => pool.liquidityTokenSymbol)))

  const collaterals = Array.from(
    new Set(
      Object.values(poolOverviewCTOs).flatMap(
        (pool) => pool.collateral?.map((c) => (c as UnderlyingCollateralCTO).assetType) || []
      )
    )
  )

  // Filter and sort pool list
  const filteredAndSortedPools = Object.values(poolOverviewCTOs)
    .filter((pool: PoolBaseOverviewCTO) => {
      const currencyMatch = currencyFilter === 'all' || pool.liquidityTokenSymbol === currencyFilter

      const collateralMatch =
        collateralFilter === 'all' ||
        pool.collateral?.some((c) => (c as UnderlyingCollateralCTO)?.assetType === collateralFilter)
      const networkMatch = networkFilter === 'all' || pool.chainConfigurationName === networkFilter

      const accessMatch = withAccessFilter && accountAddr ? accountPoolPairs[accountAddr]?.[pool.poolAddr] : true

      return currencyMatch && collateralMatch && networkMatch && accessMatch
    })
    .sort((a: PoolBaseOverviewCTO, b: PoolBaseOverviewCTO) => {
      let comparison = 0
      switch (sortBy) {
        case 'alphabetical': {
          comparison = displayName(a).localeCompare(displayName(b))
          break
        }
        case 'interest_rate': {
          const rateA = getMaximumInterestRate(a)
          const rateB = getMaximumInterestRate(b)
          comparison = rateA - rateB
          break
        }
        case 'my_principal': {
          const principalA = getMyPrincipalEarningInterest(a, accountAddr, poolAccountCTOs)
          const principalB = getMyPrincipalEarningInterest(b, accountAddr, poolAccountCTOs)
          comparison = principalA - principalB
          break
        }
        case 'total_principal': {
          const totalPrincipalA = getTotalPrincipalEarningInterest(a)
          const totalPrincipalB = getTotalPrincipalEarningInterest(b)
          comparison = totalPrincipalA - totalPrincipalB
          break
        }
        default: {
          comparison = displayName(a).localeCompare(displayName(b))
        }
      }
      return sortOrder === 'asc' ? comparison : -comparison
    })

  const poolCTOList = filteredAndSortedPools

  //  const { chainTimestamp } =useAppSelector((state: RootState) => state.chainState)

  // .filter((poolOverviewCTO: PoolOverviewCTO) => {
  //   const canInvest: boolean =
  //     poolOverviewCTO?.rolloverState !== PoolRolloverState.CreateNextLoan &&
  //     poolOverviewCTO?.rolloverState !== PoolRolloverState.EarlyRolloverFinalize &&
  //     poolOverviewCTO?.rolloverState !== PoolRolloverState.RolloverFinalize &&
  //     poolOverviewCTO?.rolloverState !== PoolRolloverState.LateRolloverFinalize &&
  //     chainTimestamp < poolOverviewCTO.requestedLoanState.depositClosingTimestamp

  //   logOT.debug('poolCTOList', canInvest, poolOverviewCTO.totalAssets)

  //   return canInvest && Number(poolOverviewCTO?.totalAssets ?? 0) < 100
  // })

  if (!isDataReady || !stageConfiguration || !chainConfiguration || isLoggingIn) {
    return <PoolListSkeleton />
  }

  // Only show "No Openings" if there are no pools at all, not when filters return zero results
  if (Object.values(poolOverviewCTOs).length === 0) {
    return (
      <>
        <Typography align="left" sx={{ fontWeight: 'bold' }} variant={'h4'}>
          Available Loan Vaults
        </Typography>
        <Typography variant="h4" gutterBottom component="div">
          No Openings
        </Typography>
      </>
    )
  }

  return (
    <Box sx={{ marginTop: 4, marginBottom: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
          padding: '0 8px',
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Available Loan Vaults
        </Typography>
        <Button variant="text" component={Link} to={'/availableVaults'} sx={{ color: theme.palette.accent1.emphasis }}>
          <Typography variant="bodyBold">View All</Typography>
        </Button>
      </Box>

      {/* Filter Section */}
      <Paper
        elevation={0}
        sx={{
          backgroundColor: theme.palette.grey[50],
          borderRadius: 2,
          padding: 3,
          marginBottom: 3,
          border: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
          <FilterList sx={{ marginRight: 1, color: theme.palette.text.secondary }} />
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            Filters:
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ marginBottom: 2 }}>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Currency</InputLabel>
              <Select
                value={currencyFilter}
                label="Currency"
                onChange={(e: SelectChangeEvent) => setCurrencyFilter(e.target.value)}
              >
                <MenuItem value="all">All...</MenuItem>
                {currencies.map((currency) => (
                  <MenuItem key={currency} value={currency}>
                    {currency}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Collateral</InputLabel>
              <Select
                value={collateralFilter}
                label="Collateral"
                onChange={(e: SelectChangeEvent) => setCollateralFilter(e.target.value)}
              >
                <MenuItem value="all">All Collateral</MenuItem>
                {collaterals.map((collateral) => (
                  <MenuItem key={collateral} value={collateral}>
                    {collateral}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort by</InputLabel>
                <Select value={sortBy} label="Sort by" onChange={(e: SelectChangeEvent) => setSortBy(e.target.value)}>
                  <MenuItem value="alphabetical">Alphabetical</MenuItem>
                  <MenuItem value="interest_rate">Interest Rate</MenuItem>
                  <MenuItem value="my_principal">My Principal Earning Interest</MenuItem>
                  <MenuItem value="total_principal">Total Principal Earning Interest</MenuItem>
                </Select>
              </FormControl>
              <IconButton
                size="small"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                sx={{
                  backgroundColor: theme.palette.grey[200],
                  '&:hover': { backgroundColor: theme.palette.grey[300] },
                }}
              >
                <KeyboardArrowUp
                  sx={{
                    transform: sortOrder === 'desc' ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                  }}
                />
              </IconButton>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2} sx={{ marginLeft: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={<Switch checked={withAccessFilter} onChange={(e) => setWithAccessFilter(e.target.checked)} />}
                label="Access"
              />
            </Box>
          </Grid>
        </Grid>

        <Button
          variant="text"
          onClick={() => {
            setCurrencyFilter('all')
            setCollateralFilter('all')
            setNetworkFilter('all')
            setSortBy('alphabetical')
            setSortOrder('asc')
          }}
          sx={{ color: theme.palette.text.secondary, textTransform: 'none' }}
        >
          Clear All
        </Button>
      </Paper>

      {/* Results count */}
      <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 2 }}>
        Showing {poolCTOList.length} of {Object.values(poolOverviewCTOs).length} vaults
      </Typography>

      {/* Show message when filters return no results, but keep filters visible */}
      {poolCTOList.length === 0 && Object.values(poolOverviewCTOs).length > 0 && (
        <Box sx={{ textAlign: 'center', padding: 4 }}>
          <Typography variant="h6" color="text.secondary" sx={{ marginBottom: 1 }}>
            No vaults match your current filters
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters or click Clear All to see all available vaults
          </Typography>
        </Box>
      )}

      {/* Show pool list only if there are results */}
      {poolCTOList.length > 0 && (
        <Box>
          {poolCTOList.map((poolOverviewCTO: PoolBaseOverviewCTO) => {
            return <PoolOpeningRow key={poolOverviewCTO.poolAddr} poolBaseOverviewCTO={poolOverviewCTO} />
          })}
        </Box>
      )}
    </Box>
  )
}
